import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionContext } from 'src/hooks'

import { analyticsEnvironment } from './config'
import {
  associateOrganization,
  identifyAccount,
  registerProperties,
  resetIdentity,
  unregisterProperties
} from './identity'
import { clearJourney } from './journey'

/**
 * Keeps the analytics identity in step with the session.
 *
 * Renders nothing — it is a side-effect component, the same shape as
 * `FlagFetchLogger` inside `FlagProvider`. Must be mounted **inside**
 * `UserSessionContextProvider`, which is why it is separate from
 * `AnalyticsProvider`: the provider has to sit above the tree, this has to sit
 * below the session.
 *
 * Sign-out is detected by the account id disappearing rather than by hooking
 * `removeUserData` — that keeps the session provider unaware of analytics, and
 * it catches every way a session can end, including expiry.
 */
export const AnalyticsIdentity = () => {
  const { user, activeWorkspaceId } = useSessionContext()
  const { i18n } = useTranslation()

  const userId = user?.user?.id

  /**
   * Last identified account. Also the "was anyone signed in?" flag — without it
   * the first render of a logged-out visitor would fire a pointless `reset()`
   * and discard the anonymous id the pre-signup funnel is built on.
   */
  const identified = useRef<number | undefined>(undefined)

  const activeOrganization = (user?.organizations ?? []).find(
    (organization) => Number(organization.id) === activeWorkspaceId
  )
  // Depended on as primitives, not as the object: the session refresh that
  // follows workspace creation hands back a new array every time, and an object
  // dependency would re-register on every refresh.
  const orgId = activeOrganization ? Number(activeOrganization.id) : undefined
  const role = activeOrganization?.access_role

  useEffect(() => {
    if (userId !== undefined) {
      if (identified.current !== userId) {
        identifyAccount(String(userId))
        identified.current = userId
      }

      return
    }

    if (identified.current !== undefined) {
      // The journey dies with the identity, and this is the whole reason:
      // `reset()` starts a new anonymous person, but `journey_id` lives in our
      // own storage and would otherwise survive. One journey spanning two people
      // makes a per-person funnel unsatisfiable — nobody completed both the first
      // step and the second — so it reports a flat zero from step 2 onward.
      // Signing out ends the attempt; the next visit starts a fresh one.
      clearJourney()
      resetIdentity()
      identified.current = undefined
    }
  }, [userId])

  // Region stripped: BR-34's working language is `ro` / `ru` / `en`, and a raw
  // `i18n.language` yields `en-US`, which would split one language across several
  // dashboard buckets. Same normalisation the signup form already applies before
  // sending a language to the API.
  const locale = i18n.language.split('-')[0]

  useEffect(() => {
    registerProperties({
      env: analyticsEnvironment,
      app_version: __APP_VERSION__,
      locale
    })
  }, [locale])

  useEffect(() => {
    if (orgId === undefined) {
      unregisterProperties(['org_id', 'role'])

      return
    }

    registerProperties({ org_id: orgId, role })
    associateOrganization(orgId)
  }, [orgId, role])

  return null
}
